<script>
    import { getContext } from "svelte";

    const { data, xGet, yGet, y, xScale, yScale, width, height } =
        getContext("LayerCake");

    /** @type {String} [stroke='#ab00d6'] - The shape's fill color. This is technically optional because it comes with a default value but you'll likely want to replace it with your own color. */
    export let stroke = "#444";
    /** @type {string} */
    export let yKey;
    /** @type {import("../scripts/charts.js").PageContext} */
    export let ctx;
    export let formatY;
    /** @type {number} */
    export let lastYear;
    import guff from "dataset:../_data/guff.aml";

    let labelLineHeight = 16;
    let dy = ctx["type"] == "institution" ? -(labelLineHeight * 3) + 3 : -10;

    $: path = getPath($xGet, $yGet, $xScale, $yScale, $data);

    function getPath(xGet, yGet, xScale, yScale, data) {
        let path3 = "";
        let curr_x;
        let curr_y;

        // this is essentially d3.curveStep, but I couldn't get that working so this is my manual way of constructing the path
        for (let i = 0; i < data.length; i++) {
            let d = data[i];
            if (i == 0) {
                path3 += "M" + xGet(d) + "," + yScale(0);
            }
            // only draw if BOTH x and y have changed from prev datapoint to new
            // datapoint. This minimizes line segments.
            else if (d[yKey] != curr_y && d.year !== curr_x) {
                path3 += "V" + yScale(curr_y); // up to prev year's top y value
                path3 += "H" + xGet(d); // right to new year's x
            }

            curr_x = d.year;
            curr_y = d[yKey];
        }

        // At the end we need to do 1-2 more line segements:

        // up to the y of the last item in the dataset
        path3 += "V" + yScale(curr_y);

        // If last notice's year is not the last year in the chart (2023 as of
        // writing), then draw horizontal line to the present
        if (curr_x !== lastYear) path3 += "H" + xScale(lastYear);

        // Add extra lines so that we can fill the shape on homepage (instead
        // of using BarStacked). We'll hide the stroke for these extra lines
        // with clip-path.
        if (ctx.type === "homepage") {
            path3 += "h5"; // horizontal line, 5px past the right side of the chart - doesn't need to be 5 but a slightly bigger number makes it easier to make sure we've hidden the whole stroke
            path3 += `V${yScale(0)}`; // vertical line down to axis
            // Don't need last line back to 0,0 because SVG assumes it for fill
        }

        return path3;
    }

    $: endY = $data[$data.length - 1]
        ? $data[$data.length - 1].cumulative_pct_complete
        : "";
</script>

{#if $data.length > 0}
    <path
        class="path-line"
        class:homepage-progress-line={ctx.type === "homepage"}
        d={path}
        {stroke}
    />
    <circle
        cx={$xScale(lastYear)}
        cy={$yGet($data[$data.length - 1])}
        r="3.5"
    />
    <text
        x={$xScale(lastYear)}
        y={$yGet($data[$data.length - 1]) + dy}
        class="end-point-label end-point-label-bkgd"
    >
        {endY < 0.0095
            ? (endY * 100).toFixed(1) + "%"
            : formatY($y($data[$data.length - 1]))}
        {ctx["type"] == "tribe"
            ? $y($data[$data.length - 1]) > 1
                ? " " + guff.inst_progress_label0
                : " remain"
            : ""}
    </text>
    <text
        x={$xScale(lastYear)}
        y={$yGet($data[$data.length - 1]) + dy}
        class="end-point-label"
    >
        {endY < 0.0095
            ? (endY * 100).toFixed(1) + "%"
            : formatY($y($data[$data.length - 1]))}
        {ctx["type"] == "tribe"
            ? $y($data[$data.length - 1]) > 1
                ? " " + guff.inst_progress_label0
                : " remain"
            : ""}
    </text>

    {#if ctx["type"] == "institution"}
        <text
            x={$xScale(lastYear)}
            y={$yGet($data[$data.length - 1]) + dy + labelLineHeight * 1}
            class="end-point-label end-point-label-bkgd"
        >
            {guff.inst_progress_label1}
        </text>
        <text
            x={$xScale(lastYear)}
            y={$yGet($data[$data.length - 1]) + dy + labelLineHeight * 2}
            class="end-point-label end-point-label-bkgd"
        >
            {guff.inst_progress_label2}
        </text>
        <text
            x={$xScale(lastYear)}
            y={$yGet($data[$data.length - 1]) + dy + labelLineHeight * 1}
            class="end-point-label"
        >
            {guff.inst_progress_label1}
        </text>
        <text
            x={$xScale(lastYear)}
            y={$yGet($data[$data.length - 1]) + dy + labelLineHeight * 2}
            class="end-point-label"
        >
            {guff.inst_progress_label2}
        </text>
    {/if}
{:else}
    <!-- Zero progress made -->
    <!-- svelte-ignore component-name-lowercase -->
    <line
        class="path-line"
        x1={$xScale(1990)}
        x2={$xScale(lastYear)}
        y1={$yScale(0)}
        y2={$yScale(0)}
        {stroke}
    />
    <circle cx={$xScale(lastYear)} cy={$yScale(0)} r="3.5" />
    <text
        x={$xScale(lastYear)}
        y={$yScale(0) + dy}
        text-anchor="middle"
        class="end-point-label end-point-label-bkgd"
    >
        0%
    </text>
    <text
        x={$xScale(lastYear)}
        y={$yScale(0) + dy}
        text-anchor="middle"
        class="end-point-label"
    >
        0%
    </text>

    {#if ctx["type"] == "institution"}
        <text
            x={$xScale(lastYear)}
            y={$yScale(0) + dy + labelLineHeight * 1}
            class="end-point-label end-point-label-bkgd"
        >
            {guff.inst_progress_label1}
        </text>
        <text
            x={$xScale(lastYear)}
            y={$yScale(0) + dy + labelLineHeight * 2}
            class="end-point-label end-point-label-bkgd"
        >
            {guff.inst_progress_label2}
        </text>
        <text
            x={$xScale(lastYear)}
            y={$yScale(0) + dy + labelLineHeight * 1}
            class="end-point-label"
        >
            {guff.inst_progress_label1}
        </text>
        <text
            x={$xScale(lastYear)}
            y={$yScale(0) + dy + labelLineHeight * 2}
            class="end-point-label"
        >
            {guff.inst_progress_label2}
        </text>
    {/if}
{/if}

<style>
    circle {
        fill: var(--color-text-body);
    }

    .path-line {
        fill: none;
        stroke: var(--color-text-body);
        stroke-linejoin: round;
        stroke-linecap: round;
        stroke-width: 1px;
    }

    .homepage-progress-line {
        fill: var(--affiliated);
        /* 5px right hides righthand stroke (used for creating the fill shape)
           -1px for everything else avoids any clipping on other sides (since
           2px stroke-wdith goes "outside" path's rectangle ) */
        clip-path: inset(-1px 5px -1px -1px);
    }

    .end-point-label {
        fill: var(--color-text-body);
        font-size: var(--scale-2);
        font-weight: 600;
        text-anchor: middle;
    }

    .end-point-label-bkgd {
        fill: white;
        stroke: white;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 8px;
        filter: drop-shadow(0px 0px 3px rgba(255, 255, 255, 0.9));
    }
</style>
